import { Fragment } from 'react';
import { withVersionHeaders } from '../../lib/changelogGroups';
import { useAuthStore } from '../../store/authStore';
import { MainLayout } from '../../components/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@werkstatt/ui';
import { Users, Clock, CalendarX, TrendingUp, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { formatDuration } from '../../lib/utils';
import type { DashboardStats } from '../../types';

interface ChangelogEntry {
  id: number;
  git_hash: string;
  title: string;
  category: string;
  scope: string | null;
  version?: string | null;
  icon: string;
  created_at: string;
}

export default function Dashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await api.get<DashboardStats>('/dashboard/');
      return res.data;
    },
  });

  const { data: changelog = [] } = useQuery({
    queryKey: ['changelog'],
    queryFn: async () => (await api.get<ChangelogEntry[]>('/changelog')).data,
    staleTime: 5 * 60 * 1000,
  });

  const statCards = [
    {
      title: 'Mitarbeiter gesamt',
      value: isLoading ? '...' : String(stats?.employees_total ?? 0),
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-500/15',
      href: '/employees',
      description: 'Aktive Mitarbeiter',
    },
    {
      title: 'Heute anwesend',
      value: isLoading ? '...' : String(stats?.employees_active_today ?? 0),
      icon: Clock,
      color: 'text-green-600',
      bgColor: 'bg-green-500/15',
      href: '/time-entries',
      description: 'Eingestempelt',
    },
    {
      title: 'Kommende Abwesenheiten',
      value: isLoading ? '...' : String(stats?.upcoming_absences?.length ?? 0),
      icon: CalendarX,
      color: 'text-orange-600',
      bgColor: 'bg-orange-500/15',
      href: '/absences',
      description: 'Nächste 7 Tage',
    },
    {
      title: 'Diese Woche (Ø)',
      value: isLoading ? '...' : (() => {
        if (!stats?.weekly_overview?.length) return '0:00 Std';
        const total = stats.weekly_overview.reduce((sum, e) => sum + e.total_minutes, 0);
        return formatDuration(Math.round(total / stats.weekly_overview.length));
      })(),
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-500/15',
      href: '/reports',
      description: 'Ø Arbeitsstunden',
    },
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl font-bold">
            Willkommen zurück, {user?.first_name || user?.email?.split('@')[0]}!
          </h1>
          <p className="text-muted-foreground mt-2">Hier ist Ihre Zeiterfassung Übersicht</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat) => (
            <Card
              key={stat.title}
              variant="glass"
              className="hover:shadow-glow transition-shadow cursor-pointer"
              onClick={() => navigate(stat.href)}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="font-display text-sm font-medium">{stat.title}</CardTitle>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="font-display">Wochenübersicht</CardTitle>
              <CardDescription>
                {stats ? `${stats.week_monday} – ${stats.week_sunday}` : 'Aktuelle Woche'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-muted-foreground text-sm">Lade...</p>
              ) : !stats?.weekly_overview?.length ? (
                <p className="text-muted-foreground text-sm">Keine Daten für diese Woche</p>
              ) : (
                <div className="space-y-2">
                  {stats.weekly_overview.slice(0, 8).map((row) => {
                    const targetMinutes = (row.employee.hours_per_week / 5) * 5 * 60;
                    const pct = targetMinutes > 0 ? Math.min(100, Math.round((row.total_minutes / targetMinutes) * 100)) : 0;
                    return (
                      <div key={row.employee.id} className="flex items-center gap-3">
                        <div className="w-32 text-sm truncate font-medium">
                          {row.employee.first_name} {row.employee.last_name}
                        </div>
                        <div className="flex-1 bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="text-xs text-muted-foreground w-16 text-right">
                          {formatDuration(row.total_minutes)}
                        </div>
                        <div className="text-xs text-muted-foreground w-8 text-right">
                          {row.days_present}T
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card variant="glass">
            <CardHeader>
              <CardTitle className="font-display">Kommende Abwesenheiten</CardTitle>
              <CardDescription>Nächste 7 Tage</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-muted-foreground text-sm">Lade...</p>
              ) : !stats?.upcoming_absences?.length ? (
                <div className="text-center py-6 text-muted-foreground">
                  <CalendarX className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Keine Abwesenheiten geplant</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {stats.upcoming_absences.map((item, idx) => {
                    const typeLabels: Record<string, string> = {
                      vacation: 'Urlaub', sick: 'Krank', special: 'Sonderurlaub', other: 'Sonstiges'
                    };
                    return (
                      <div key={idx} className="flex items-center justify-between p-2 rounded-md bg-muted/50">
                        <div>
                          <p className="text-sm font-medium">{item.employee.first_name} {item.employee.last_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.absence.start_date} → {item.absence.end_date}
                          </p>
                        </div>
                        <span className="text-xs bg-orange-500/15 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-pill">
                          {typeLabels[item.absence.absence_type] || item.absence.absence_type}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {changelog.length > 0 && (
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2 text-base">
                <Wrench className="h-4 w-4 text-muted-foreground" />
                Letzte Änderungen & Updates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {withVersionHeaders(changelog.slice(0, 20)).map(({ entry, header }) => {
                  const categoryBadge: Record<string, { label: string; cls: string }> = {
                    feature:     { label: 'Feature',  cls: 'bg-blue-500/15 text-blue-600' },
                    bugfix:      { label: 'Bug',      cls: 'bg-red-500/15 text-red-600' },
                    improvement: { label: 'Update',   cls: 'bg-amber-500/15 text-amber-600' },
                    security:    { label: 'Security', cls: 'bg-orange-500/15 text-orange-600' },
                  };
                  const badge = categoryBadge[entry.category]
                    ?? { label: entry.category, cls: 'bg-muted text-muted-foreground' };
                  return (
                    <Fragment key={entry.id}>
                    {header && (
                      <li className="pt-2 first:pt-0 font-mono text-xs font-semibold text-muted-foreground">v{header}</li>
                    )}
                    <li className="flex items-start gap-2 text-sm">
                      <span className="shrink-0 mt-0.5">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-pill ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </span>
                      <span className="text-foreground min-w-0 flex-1">{entry.title}</span>
                      <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(entry.created_at).toLocaleDateString('de-DE', {
                          day: '2-digit', month: '2-digit', year: '2-digit',
                        })}
                      </span>
                    </li>
                    </Fragment>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}
